import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Table } from "../../../../components/Table";
import { TableTypes } from "../../../../components/Table/Table.types";
import { CompanyStatus, UserPermissions } from "../../../../enums";
import { Api } from "../../../../services/api";
import { Company } from "../../../../services/api/types/Companies";
import { Office } from "../../../../services/api/types/Offices";
import { getColumns } from "./columnData";
import { CompanyForm } from "../CompanyForm";
import {
  CompanyFormHeader,
  CompanyFormType,
} from "../CompanyForm/companyFormSchema";
import { ColumnContainer } from "../../Admin.styles";
import { useAppSelector } from "../../../../store/hooks";
import axios from "axios";
import { FormikHelpers } from "formik";

export const EditCompanyForm: React.FC<{}> = () => {
  const defaultCompany: CompanyFormType = {
    name: "",
    address: "",
    entityId: "",
    status: "",
  };
  const history = useHistory();
  const userPermissions = useAppSelector(state => state.auth.user.permissions);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<Array<Office>>([]);
  const [company, setCompany] = useState<CompanyFormType>(defaultCompany);
  const selectedRows = useRef<Array<Office>>([]);

  const { companyId } = useParams<{ companyId: string }>();

  useEffect(() => {
    fetchCompany();
  }, [companyId]);

  const fetchCompany = async () => {
    try {
      const response = await Api.companies.getCompany(companyId);
      response.data.status = CompanyStatus[response.data.status as number];
      const data: CompanyFormType = {
        name: response.data.name,
        address: response.data.address,
        entityId: response.data.entityId,
        status: response.data.status,
      };
      setCompany(data);
      setTableData(response.data.offices);
      setIsLoading(false);
    } catch (e) {
      console.error("Fetch Company Error:", e);
    }
  };
  const submitValues = (
    values: CompanyFormType,
    { setErrors }: FormikHelpers<CompanyFormType>
  ) => {
    const data: Partial<Company> = {
      status:
        values.status === "Active"
          ? CompanyStatus.Active
          : CompanyStatus.Inactive,
      name: values.name,
      address: values.address,
      entityId: values.entityId,
    };
    Api.companies
      .updateCompany(companyId, data)
      .then(() => {
        const currentLocation = history.location;
        currentLocation.pathname = "/admin/companies";
        history.push(currentLocation);
      })
      .catch((error: Error) => {
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.data.detail ===
            'duplicate key value violates unique constraint "companies_name_key"'
        ) {
          setErrors({ name: "Name already used" });
        } else {
          console.error("Create Company", error);
        }
      });
  };

  let handleAddOffice;
  if (userPermissions.includes(UserPermissions.OfficeAddOffice)) {
    handleAddOffice = () => {
      const currentLocation = history.location;
      currentLocation.pathname = "/admin/offices/add";
      history.push(currentLocation);
    };
  }

  const handleDeleteSelected = () => {
    //delete company from office
    if (!userPermissions.includes(UserPermissions.OfficeManageDetails)) return;
    for (const office of selectedRows.current) {
      Api.offices
        .getOffice(office.id)
        .then(response => {
          const officeCompanies = response.data.companies;
          const newCompanies: Array<string> = [];
          for (const company of officeCompanies) {
            if (company.associated && company.id !== companyId) {
              newCompanies.push(company.id);
            }
          }
          Api.offices
            .updateOffice(office.id, { companyIds: newCompanies })
            .catch(error => console.error(error));
        })
        .catch(error => console.error(error));
    }
    setTableData(
      tableData.filter(
        office =>
          selectedRows.current.findIndex(curr => curr.id === office.id) === -1
      )
    );
  };

  return (
    <ColumnContainer>
      {!isLoading && (
        <>
          <CompanyForm
            header={CompanyFormHeader.edit}
            submitValues={submitValues}
            value={company}
            canSave={userPermissions.includes(
              UserPermissions.CompanyManageDetails
            )}
          />
          <div style={{ width: "100%" }}>
            <Table
              searchable
              editable
              title={TableTypes.Offices}
              tableType={TableTypes.Offices}
              isLoading={isLoading}
              defaultRows={!tableData.length ? 0 : 20}
              columns={getColumns()}
              rowsPerPage={[10, 20, 35, 50]}
              selectedRows={selectedRows}
              data={tableData}
              handleAddButton={handleAddOffice}
              handleDeleteSelected={handleDeleteSelected}
            />
          </div>
        </>
      )}
    </ColumnContainer>
  );
};
